import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Maximize, RotateCw, Sparkles, Brain, Box, Smartphone, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import '@google/model-viewer';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const Viewer3D = () => {
  const { markerId } = useParams();
  const navigate = useNavigate();
  const [marker, setMarker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const modelViewerRef = useRef(null);

  useEffect(() => {
    loadMarker();
    logEvent('view_3d');
  }, [markerId]);

  const loadMarker = async () => {
    try {
      const response = await api.get(`/markers/${markerId}`);
      let markerData = response.data;
      if (markerData.model_3d_id) {
        const modelRes = await api.get('/models');
        const model = modelRes.data.find((m) => m.id === markerData.model_3d_id);
        markerData.model_3d = model;
      }
      const quizRes = await api.get('/quizzes', { params: { marker_id: markerId } });
      if (quizRes.data.length > 0) markerData.quiz = quizRes.data[0];
      setMarker(markerData);
    } catch (error) {
      toast.error('Failed to load 3D content');
    } finally {
      setLoading(false);
    }
  };

  const logEvent = async (eventType) => {
    try {
      await api.post('/analytics/events', { event_type: eventType, marker_id: markerId, ar_success: eventType === 'view_ar' });
    } catch {}
  };

  const handleARMode = () => {
    if (modelViewerRef.current?.canActivateAR) {
      modelViewerRef.current.activateAR();
      logEvent('view_ar');
      toast.success('AR mode activated!');
    } else {
      toast.info('AR not supported on this device. Showing 3D view.');
    }
  };

  const handleQuizSubmit = async () => {
    if (!marker.quiz) return;
    const answers = Object.entries(quizAnswers).map(([questionId, selectedOption]) => ({
      question_id: questionId, selected_option: parseInt(selectedOption),
    }));
    try {
      const response = await api.post('/quiz-attempts', { quiz_id: marker.quiz.id, marker_id: markerId, answers });
      setQuizResult(response.data);
      toast.success(`Score: ${Math.round(response.data.score)}%`);
    } catch (error) {
      toast.error('Failed to submit quiz');
    }
  };

  if (loading) return <Layout><div className="text-center py-12 text-slate-500">Loading 3D viewer...</div></Layout>;
  if (!marker) return <Layout><div className="text-center py-12 text-slate-500">Marker not found</div></Layout>;

  const hasModel = marker.model_3d?.file_url;
  const modelUrl = hasModel ? `${BACKEND}${marker.model_3d.file_url}` : null;

  return (
    <Layout title="3D Viewer">
      <div className="space-y-4 sm:space-y-6">
        {/* Top bar */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <Button data-testid="back-btn" variant="ghost" onClick={() => navigate(-1)} className="rounded-full hover:bg-indigo-50">
            <ArrowLeft className="w-4 h-4 mr-1 sm:mr-2" /> <span className="hidden sm:inline">Back</span>
          </Button>
          <div className="flex gap-2 flex-wrap">
            {hasModel && (
              <Button data-testid="ar-mode-btn" onClick={handleARMode}
                className="rounded-full bg-gradient-to-r from-indigo-600 to-cyan-500 hover:from-indigo-700 hover:to-cyan-600 text-white text-xs sm:text-sm">
                <Sparkles className="w-4 h-4 mr-1" /> View in AR
              </Button>
            )}
            <Button data-testid="quick-check-btn" variant="outline" onClick={() => setShowQuiz(!showQuiz)}
              className="rounded-full hover:bg-amber-50 border-amber-200 text-xs sm:text-sm" disabled={!marker.quiz}>
              <Brain className="w-4 h-4 mr-1" /> Quick Check
            </Button>
          </div>
        </div>

        {/* 3D Viewer Card */}
        <Card className="border-slate-200 shadow-xl overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-indigo-50 to-cyan-50 p-4 sm:p-6">
            <CardTitle className="text-indigo-900 flex items-center gap-2 text-base sm:text-lg">
              <Box className="w-5 h-5" /> {marker.concept_title}
            </CardTitle>
            <p className="text-slate-600 text-xs sm:text-sm">{marker.concept_description}</p>
          </CardHeader>
          <CardContent className="p-0">
            {hasModel ? (
              <>
                <div className="relative" style={{ height: 'min(70vh, 500px)' }}>
                  <model-viewer
                    ref={modelViewerRef}
                    src={modelUrl}
                    alt={marker.concept_title}
                    ar ar-modes="webxr scene-viewer quick-look"
                    camera-controls touch-action="pan-y"
                    auto-rotate auto-rotate-delay="3000" rotation-per-second="30deg"
                    environment-image="neutral" shadow-intensity="1" exposure="1"
                    style={{ width: '100%', height: '100%', background: 'linear-gradient(to bottom, #f8fafc, #e2e8f0)' }}
                    loading="eager" reveal="auto"
                  >
                    <button slot="ar-button" className="absolute bottom-4 right-4 bg-gradient-to-r from-indigo-600 to-cyan-500 text-white px-4 py-2 sm:px-6 sm:py-3 rounded-full font-bold shadow-lg flex items-center gap-2 text-sm">
                      <Smartphone className="w-4 h-4" /> View in AR
                    </button>
                    <div className="absolute top-3 left-3 glass p-2 rounded-lg hidden sm:block">
                      <p className="text-xs text-slate-700">Drag to Rotate | Pinch to Zoom</p>
                    </div>
                  </model-viewer>
                </div>
                <div className="p-3 bg-slate-50 border-t border-slate-200 flex gap-2 justify-center">
                  <Button variant="ghost" size="sm" className="rounded-full hover:bg-indigo-50 text-xs"
                    onClick={() => { modelViewerRef.current?.resetTurntableRotation(); toast.success('View reset'); }}>
                    <RotateCw className="w-4 h-4 mr-1" /> Reset
                  </Button>
                  <Button variant="ghost" size="sm" className="rounded-full hover:bg-indigo-50 text-xs"
                    onClick={() => modelViewerRef.current?.requestFullscreen?.()}>
                    <Maximize className="w-4 h-4 mr-1" /> Fullscreen
                  </Button>
                </div>
              </>
            ) : (
              <div className="p-8 sm:p-16 text-center bg-gradient-to-b from-slate-50 to-white">
                <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-indigo-100 flex items-center justify-center">
                  <Box className="w-10 h-10 text-indigo-400" />
                </div>
                <h3 className="text-lg font-bold text-slate-700 mb-2">3D Model Not Yet Uploaded</h3>
                <p className="text-sm text-slate-500 max-w-md mx-auto">
                  The 3D model for this concept hasn't been uploaded yet. Ask your Content Admin to upload a .glb or .gltf file for this marker.
                </p>
                {marker.image_url && (
                  <div className="mt-6 max-w-sm mx-auto">
                    <img src={marker.image_url} alt={marker.concept_title} className="w-full rounded-lg shadow-md" />
                    <p className="text-xs text-slate-400 mt-2">Marker Reference Image</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* AR Info */}
        {hasModel && (
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-bold text-blue-900 mb-1 text-xs sm:text-sm">AR Experience</h3>
                  <p className="text-xs text-blue-800">
                    Click "View in AR" to see this model in your environment. Works on iOS and Android with AR support.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quiz Section */}
        {showQuiz && marker.quiz && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card data-testid="quiz-card" className="border-amber-200 bg-amber-50/50">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="flex items-center gap-2 text-amber-900 text-base sm:text-lg">
                  <Brain className="w-5 h-5 text-amber-600" /> Quick Check Quiz
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 sm:space-y-6 p-4 sm:p-6 pt-0">
                {marker.quiz.questions.map((question, qIndex) => (
                  <div key={question.id} className="space-y-2 sm:space-y-3">
                    <p className="font-medium text-slate-800 text-sm sm:text-base">{qIndex + 1}. {question.text}</p>
                    <div className="space-y-2">
                      {question.options.map((option, oIndex) => (
                        <label key={oIndex}
                          className={`flex items-center gap-3 p-2.5 sm:p-3 rounded-lg border cursor-pointer transition-colors text-sm
                            ${quizAnswers[question.id] == oIndex
                              ? 'border-indigo-400 bg-indigo-50'
                              : 'border-slate-200 bg-white hover:bg-indigo-50 hover:border-indigo-300'}`}>
                          <input type="radio" name={question.id} value={oIndex}
                            checked={quizAnswers[question.id] == oIndex}
                            onChange={(e) => setQuizAnswers({ ...quizAnswers, [question.id]: e.target.value })}
                            className="w-4 h-4 text-indigo-600" />
                          <span className="text-slate-700">{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
                <Button data-testid="submit-quiz-btn" onClick={handleQuizSubmit}
                  className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700"
                  disabled={Object.keys(quizAnswers).length !== marker.quiz.questions.length}>
                  Submit Quiz
                </Button>
                {quizResult && (
                  <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                    className="p-4 sm:p-6 bg-white rounded-lg border-2 border-indigo-200 text-center">
                    <div className="text-4xl sm:text-5xl font-bold text-indigo-600 mb-2">{Math.round(quizResult.score)}%</div>
                    <p className="text-base sm:text-lg font-bold text-indigo-900 mb-1">
                      {quizResult.score >= 70 ? 'Great Job!' : 'Keep Practicing!'}
                    </p>
                    <p className="text-xs sm:text-sm text-slate-600">
                      {quizResult.first_attempt ? 'First attempt recorded!' : 'Practice attempt'}
                    </p>
                  </motion.div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </Layout>
  );
};
